import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { LANGUAGES } from '../../../../i18n/menuLanguages';
import { trackEvent } from '../../../../lib/analytics';

import {
    selectTheme,
    selectLanguage,
    selectTimezone,
    selectNotificationsDiagramShared,
    selectNotificationsMentions,
    selectNotificationsProductUpdates,
    selectDiagramPrefs,
} from '../../../../redux/preferences/preferences.selectors';
import { preferencesPatchRequest } from '../../../../redux/preferences/preferences.actions';

import SettingsDropdown from '../_components/SettingsDropdown';

/* ── option lists ──────────────────────────────────────────── */

// Theme/input/background/grid/show-hide/on-off option labels are built inside
// the component (buildXOptions(t)) so they can go through t() — they used to
// be plain module-level constants before i18n.
const buildThemeOptions = (t) => [
    { value: 'dark', label: t('settings_page.preferences.theme_dark') },
    { value: 'light', label: t('settings_page.preferences.theme_light') },
    { value: 'system', label: t('settings_page.preferences.theme_system') },
];

const LANGUAGE_OPTIONS = LANGUAGES.map((l) => ({ value: l.code, label: l.native }));

// Curated list (not the full IANA set) — each entry's display label is a full
// i18n string (GMT offset + city/region) so it goes through t() just like the
// other buildXOptions(t) lists below. The GMT±HH:MM offset notation itself is
// left untranslated in every locale (it is a universal technical convention);
// only the city/region name after the em dash varies per locale.
const buildTimezoneOptions = (t) => [
    { value: 'Pacific/Honolulu', label: t('settings_page.preferences.timezone_options.honolulu') },
    { value: 'America/Anchorage', label: t('settings_page.preferences.timezone_options.anchorage') },
    { value: 'America/Los_Angeles', label: t('settings_page.preferences.timezone_options.los_angeles') },
    { value: 'America/Denver', label: t('settings_page.preferences.timezone_options.denver') },
    { value: 'America/Chicago', label: t('settings_page.preferences.timezone_options.chicago') },
    { value: 'America/New_York', label: t('settings_page.preferences.timezone_options.new_york') },
    { value: 'America/Caracas', label: t('settings_page.preferences.timezone_options.caracas') },
    { value: 'America/Sao_Paulo', label: t('settings_page.preferences.timezone_options.sao_paulo') },
    { value: 'Atlantic/Azores', label: t('settings_page.preferences.timezone_options.azores') },
    { value: 'Europe/London', label: t('settings_page.preferences.timezone_options.london') },
    { value: 'Europe/Paris', label: t('settings_page.preferences.timezone_options.paris') },
    { value: 'Europe/Kyiv', label: t('settings_page.preferences.timezone_options.kyiv') },
    { value: 'Europe/Istanbul', label: t('settings_page.preferences.timezone_options.istanbul') },
    { value: 'Asia/Dubai', label: t('settings_page.preferences.timezone_options.dubai') },
    { value: 'Asia/Karachi', label: t('settings_page.preferences.timezone_options.karachi') },
    { value: 'Asia/Kolkata', label: t('settings_page.preferences.timezone_options.kolkata') },
    { value: 'Asia/Dhaka', label: t('settings_page.preferences.timezone_options.dhaka') },
    { value: 'Asia/Bangkok', label: t('settings_page.preferences.timezone_options.bangkok') },
    { value: 'Asia/Shanghai', label: t('settings_page.preferences.timezone_options.shanghai') },
    { value: 'Asia/Tokyo', label: t('settings_page.preferences.timezone_options.tokyo') },
    { value: 'Australia/Sydney', label: t('settings_page.preferences.timezone_options.sydney') },
    { value: 'Pacific/Auckland', label: t('settings_page.preferences.timezone_options.auckland') },
];

const buildInputModeOptions = (t) => [
    { value: 'auto', label: t('dropdown_ctrl_settings.opt_auto') },
    { value: 'trackpad', label: t('dropdown_ctrl_settings.opt_trackpad') },
    { value: 'mouse', label: t('dropdown_ctrl_settings.opt_mouse') },
];

const buildBackgroundOptions = (t) => [
    { value: 'default', label: t('t.ctx.base.colors.default') },
    { value: 'gray', label: t('dropdown_ctrl_settings.opt_gray') },
    { value: 'blue', label: t('dropdown_ctrl_settings.opt_blue') },
    { value: 'purple', label: t('dropdown_ctrl_settings.opt_purple') },
    { value: 'violet', label: t('dropdown_ctrl_settings.opt_violet') },
    { value: 'green', label: t('dropdown_ctrl_settings.opt_green') },
    { value: 'red', label: t('dropdown_ctrl_settings.opt_red') },
    { value: 'yellow', label: t('dropdown_ctrl_settings.opt_yellow') },
];

const buildGridOptions = (t) => [
    { value: 'none', label: t('dropdown_ctrl_settings.opt_none') },
    { value: 'lines', label: t('dropdown_ctrl_settings.opt_lines') },
    { value: 'dots', label: t('dropdown_ctrl_settings.opt_dots') },
];

const buildShowHideOptions = (t) => [
    { value: true, label: t('settings_page.preferences.show') },
    { value: false, label: t('settings_page.preferences.hide') },
];

const buildOnOffOptions = (t) => [
    { value: true, label: t('settings_page.preferences.on') },
    { value: false, label: t('settings_page.preferences.off') },
];

/* ── main component ────────────────────────────────────────── */

const SettingsPreferences = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const theme = useSelector(selectTheme);
    const language = useSelector(selectLanguage);
    const timezone = useSelector(selectTimezone);
    const notifShared = useSelector(selectNotificationsDiagramShared);
    const notifMentions = useSelector(selectNotificationsMentions);
    const notifUpdates = useSelector(selectNotificationsProductUpdates);
    const diagram = useSelector(selectDiagramPrefs);

    const patchPref = (trackKey, partial) => {
        trackEvent(`app_settings_pref_${trackKey}`, {
            page: 'app',
            type: 'settings',
            subtype: 'preferences',
        });
        dispatch(preferencesPatchRequest(partial));
    };

    const THEME_OPTIONS = buildThemeOptions(t);
    const TIMEZONE_OPTIONS = buildTimezoneOptions(t);
    const INPUT_MODE_OPTIONS = buildInputModeOptions(t);
    const BACKGROUND_OPTIONS = buildBackgroundOptions(t);
    const GRID_OPTIONS = buildGridOptions(t);
    const SHOW_HIDE_OPTIONS = buildShowHideOptions(t);
    const ON_OFF_OPTIONS = buildOnOffOptions(t);

    const sections = [
        {
            title: t('settings_page.preferences.section_general'),
            rows: [
                { key: 'theme', label: t('settings_page.preferences.theme_label'), sub: t('settings_page.preferences.theme_sub'), value: theme, options: THEME_OPTIONS, onSelect: (v) => patchPref('theme', { theme: v }) },
                { key: 'language', label: t('dropdown_ctrl_settings.language'), sub: t('settings_page.preferences.language_sub'), value: language, options: LANGUAGE_OPTIONS, onSelect: (v) => { i18n.changeLanguage(v); patchPref('language', { language: v }); } },
                // { key: 'timezone', label: t('settings_page.preferences.timezone_label'), sub: t('settings_page.preferences.timezone_sub'), value: timezone, options: TIMEZONE_OPTIONS, onSelect: (v) => patchPref('timezone', { timezone: v }) },
            ],
        },
        {
            title: t('settings_page.preferences.section_diagram_defaults'),
            rows: [
                { key: 'input_mode', label: t('dropdown_ctrl_settings.mouse_trackpad'), sub: t('settings_page.preferences.input_mode_sub'), value: diagram.input_mode, options: INPUT_MODE_OPTIONS, onSelect: (v) => patchPref('input_mode', { diagram: { input_mode: v } }) },
                { key: 'background', label: t('dropdown_ctrl_settings.background_color'), sub: t('settings_page.preferences.background_sub'), value: diagram.background, options: BACKGROUND_OPTIONS, onSelect: (v) => patchPref('background', { diagram: { background: v } }) },
                { key: 'grid', label: t('dropdown_ctrl_settings.grid_layout'), sub: t('settings_page.preferences.grid_sub'), value: diagram.grid, options: GRID_OPTIONS, onSelect: (v) => patchPref('grid', { diagram: { grid: v } }) },
            ],
        },
        {
            title: t('settings_page.preferences.section_diagram_views'),
            rows: [
                { key: 'show_align_lines', label: t('dropdown_ctrl_settings.show_align_lines'), sub: t('settings_page.preferences.show_align_lines_sub'), value: diagram.show_align_lines, options: SHOW_HIDE_OPTIONS, onSelect: (v) => patchPref('show_align_lines', { diagram: { show_align_lines: v } }) },
                { key: 'show_cursors', label: t('dropdown_ctrl_settings.show_members_cursors'), sub: t('settings_page.preferences.show_cursors_sub'), value: diagram.show_cursors, options: SHOW_HIDE_OPTIONS, onSelect: (v) => patchPref('show_cursors', { diagram: { show_cursors: v } }) },
                { key: 'show_comments', label: t('dropdown_ctrl_settings.show_comments'), sub: t('settings_page.preferences.show_comments_sub'), value: diagram.show_comments, options: SHOW_HIDE_OPTIONS, onSelect: (v) => patchPref('show_comments', { diagram: { show_comments: v } }) },
                { key: 'show_scrollbars', label: t('dropdown_ctrl_settings.show_scrollbars'), sub: t('settings_page.preferences.show_scrollbars_sub'), value: diagram.show_scrollbars, options: SHOW_HIDE_OPTIONS, onSelect: (v) => patchPref('show_scrollbars', { diagram: { show_scrollbars: v } }) },
                { key: 'show_undo_redo', label: t('settings_page.preferences.show_undo_redo_label'), sub: t('settings_page.preferences.show_undo_redo_sub'), value: diagram.show_undo_redo, options: SHOW_HIDE_OPTIONS, onSelect: (v) => patchPref('show_undo_redo', { diagram: { show_undo_redo: v } }) },
            ],
        },
        {
            title: t('settings_page.preferences.section_notifications'),
            rows: [
                { key: 'notifications_diagram_shared', label: t('settings_page.preferences.notif_shared_label'), sub: t('settings_page.preferences.notif_shared_sub'), value: notifShared, options: ON_OFF_OPTIONS, onSelect: (v) => patchPref('notifications_diagram_shared', { notifications_diagram_shared: v }) },
                { key: 'notifications_mentions', label: t('settings_page.preferences.notif_mentions_label'), sub: t('settings_page.preferences.notif_mentions_sub'), value: notifMentions, options: ON_OFF_OPTIONS, onSelect: (v) => patchPref('notifications_mentions', { notifications_mentions: v }) },
                { key: 'notifications_product_updates', label: t('settings_page.preferences.notif_updates_label'), sub: t('settings_page.preferences.notif_updates_sub'), value: notifUpdates, options: ON_OFF_OPTIONS, onSelect: (v) => patchPref('notifications_product_updates', { notifications_product_updates: v }) },
            ],
        },
    ];

    return (
        <div className='settings-wrap'>
            <div className='settings-header'>{t('dropdown_ctrl_settings.preferences')}</div>

            {sections.map((section, sIdx) => (
                <React.Fragment key={section.title}>
                    <div className='settings-subheader' style={sIdx === 0 ? { marginTop: '10px' } : undefined}>{section.title}</div>
                    <div className='settings-section'>
                        {section.rows.map((row, rIdx) => (
                            <React.Fragment key={row.key}>
                                {rIdx > 0 && <div className="settings-field-line" style={{ marginTop: '2px' }}></div>}
                                <div className='settings-row'>
                                    <div className='settings-row-left'>
                                        <span className='settings-row-label'>{row.label}</span>
                                        <span className='settings-row-sub'>{row.sub}</span>
                                    </div>
                                    <SettingsDropdown value={row.value} options={row.options} onSelect={row.onSelect} />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

export default SettingsPreferences;
